import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { MyArvatoEnvironment } from '@myarvato/shared/models';
import { KpiHistoryDto } from '../models/kpi-history-dto';
import { KpiHistorySearchDto } from '../models/kpi-history-search-dto';

@Injectable({ providedIn: 'root' })
export class KpiHistoryService {
  // Injections
  private readonly environment = inject(MyArvatoEnvironment);
  private readonly httpClient = inject(HttpClient);

  // Fields
  private readonly baseUrl = `${this.environment.url.api}Transport/KpiHistory`;
  private readonly cache = new Map<string, Observable<KpiHistoryDto>>();

  /**
   * Retrieves cached KPI history data based on the provided filter.
   * If the data is not in the cache or has expired, it fetches fresh data from the server.
   * @param filter - The filter criteria for loading KPI history data.
   * @returns An observable emitting the KPI history data (`KpiHistoryDto`).
   */
  public getCachedKpis(filter: KpiHistorySearchDto): Observable<KpiHistoryDto> {
    const key = JSON.stringify(filter);

    const cachedObservable = this.cache.get(key);
    if (cachedObservable) {
      return cachedObservable;
    }

    const req$ = this.getKpis(filter).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: false,
      }),
      catchError((err) => {
        this.cache.delete(key);
        return throwError(() => err);
      }),
    );

    this.cache.set(key, req$);
    return req$;
  }

  /**
   * Invalidates the cache for KPI history data.
   * If a filter is provided, only the cache entry corresponding to that filter is invalidated.
   * If no filter is provided, the entire cache is cleared.
   * @param filter - (Optional) The filter criteria for which to invalidate the cache.
   */
  public invalidateCache(filter?: KpiHistorySearchDto) {
    if (!filter) {
      this.cache.clear();
    } else {
      this.cache.delete(JSON.stringify(filter));
    }
  }

  /**
   * Retrieves KPI history data based on the provided filter.
   * @param filter - The filter criteria for loading KPI history data.
   * @returns An observable emitting the KPI history data (`KpiHistoryDto`).
   */
  public getKpis(filter: KpiHistorySearchDto): Observable<KpiHistoryDto> {
    return this.httpClient.post<KpiHistoryDto>(`${this.baseUrl}`, filter, this.getRequestOptions());
  }

  private getRequestOptions() {
    const headers = new HttpHeaders({
      'x-skip-all-interceptor-handling': 'true',
      'x-skip-loading-interceptor-handling': 'true',
    });
    return { headers };
  }
}
